import { create } from "zustand";
import firebase from "@services/firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit as fsLimit,
  onSnapshot,
} from "firebase/firestore";
import { notificationsApi } from "@services/api/notifications";

export type InAppEventType =
  | "payment_received"
  | "booking_created"
  | "document_signed"
  | "new_message"
  | "devis_signed"
  | "ordre_status_changed"
  | "avis_received"
  | "devis_auto_cancelled"
  | "mention";

export type InAppNotification = {
  id: string;
  type: InAppEventType;
  title: string;
  body: string;
  href: string;
  read: boolean;
  createdAt: string;
};

type InAppNotificationsState = {
  notifications: InAppNotification[];
  _initialized: boolean;
  _unsub: (() => void) | null;

  /** Fetch notifications from API and start Firestore real-time listener. */
  init: (userId: string) => void;

  /** Stop the Firestore listener. */
  cleanup: () => void;

  markRead: (id: string) => void;
  markAllRead: () => void;
  dismiss: (id: string) => void;
  clearAll: () => void;
};

export const useInAppNotificationsStore = create<InAppNotificationsState>()(
  (set, get) => ({
    notifications: [],
    _initialized: false,
    _unsub: null,

    init: (userId: string) => {
      if (get()._initialized) return;
      set({ _initialized: true });

      // Fetch existing notifications from API
      notificationsApi
        .fetchAll()
        .then((notifications) => set({ notifications }))
        .catch((err) =>
          console.warn("Failed to fetch notifications:", err),
        );

      // Real-time listener for new notifications
      const notifQuery = query(
        collection(firebase.db, "user_notifications"),
        where("user_id", "==", userId),
        orderBy("created_at", "desc"),
        fsLimit(1),
      );

      let initialLoad = true;
      const unsub = onSnapshot(
        notifQuery,
        (snapshot) => {
          if (initialLoad) {
            initialLoad = false;
            return;
          }
          for (const change of snapshot.docChanges()) {
            if (change.type !== "added") continue;
            const doc = change.doc.data();
            const notif: InAppNotification = {
              id: change.doc.id,
              type: doc.type as InAppEventType,
              title: doc.title || "",
              body: doc.body || "",
              href: doc.href || "/",
              read: doc.read ?? false,
              createdAt: doc.created_at || new Date().toISOString(),
            };
            // Avoid duplicates (may already be in the API-fetched list)
            set((s) => {
              if (s.notifications.some((n) => n.id === notif.id)) return s;
              return {
                notifications: [notif, ...s.notifications].slice(0, 50),
              };
            });
          }
        },
        (err) => {
          console.warn("User notifications Firestore listener error:", err);
        },
      );

      set({ _unsub: unsub });
    },

    cleanup: () => {
      const unsub = get()._unsub;
      if (unsub) unsub();
      set({ _unsub: null, _initialized: false, notifications: [] });
    },

    markRead: (id) => {
      set((s) => ({
        notifications: s.notifications.map((n) =>
          n.id === id ? { ...n, read: true } : n,
        ),
      }));
      notificationsApi.markRead(id).catch((err) =>
        console.warn("Failed to mark notification read:", err),
      );
    },

    markAllRead: () => {
      set((s) => ({
        notifications: s.notifications.map((n) => ({ ...n, read: true })),
      }));
      notificationsApi.markAllRead().catch((err) =>
        console.warn("Failed to mark all notifications read:", err),
      );
    },

    dismiss: (id) => {
      set((s) => ({
        notifications: s.notifications.filter((n) => n.id !== id),
      }));
      notificationsApi.dismiss(id).catch((err) =>
        console.warn("Failed to dismiss notification:", err),
      );
    },

    clearAll: () => {
      set({ notifications: [] });
      notificationsApi.clearAll().catch((err) =>
        console.warn("Failed to clear all notifications:", err),
      );
    },
  }),
);
